export type Role = 'admin' | 'member';

export type SessionUser = {
  id: string;
  email: string;
  name: string;
  role: Role;
  department: string;
  mustChangePassword: boolean;
};

export type Department = {
  id: string;
  name: string;
  slug: string;
  summary: string;
  sort_order: number;
};

export type Application = {
  id: string;
  department_id: string;
  name: string;
  slug: string;
  tagline: string;
  description: string;
  audience: string;
  status: 'live' | 'beta' | 'planned' | 'maintenance';
  url: string | null;
  stack: string;
  version: string;
  sort_order: number;
  logo_url?: string | null;
  category?: AppCategory;
  /** JSON array of image URLs, as stored. Read it with appScreenshots(). */
  screenshots?: string | null;
  developer?: string;
  time_saved?: string;
  /** JSON array of benefit statements, as stored. Read it with appBenefits(). */
  benefits?: string | null;
  /** 1 = listed on the Status page, 0 = hidden. */
  show_on_status?: number;
  cost_saved?: string;
  impact_note?: string;
};

function parseList(raw: string | null | undefined): string[] {
  try {
    const v = JSON.parse(raw || '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0) : [];
  } catch {
    return [];
  }
}

/**
 * Yearly saving in S$ read from an app's cost estimate (e.g. "≈ S$13,000 a year").
 * Returns null when there is no yearly S$ figure, or the figure is marked projected.
 */
export function yearlySaving(app: Pick<Application, 'cost_saved'>): number | null {
  const text = app.cost_saved ?? '';
  if (!/year/i.test(text) || /projected/i.test(text)) return null;
  const m = text.match(/S\$\s*([\d,]+(?:\.\d+)?)\s*(k\b)?/i);
  if (!m) return null;
  const n = Number(m[1].replace(/,/g, '')) * (m[2] ? 1000 : 1);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Services as the public pages should show them: an open incident on a service still
 * marked Operational shows that service as Degraded, with the incident title as its message.
 */
export function withOpenIncidents(services: ServiceStatus[], incidents: Incident[]): ServiceStatus[] {
  const open = incidents.filter((i) => !i.resolved_at);
  return services.map((svc) => {
    const inc = svc.status === 'operational' ? open.find((i) => i.service_id === svc.id) : undefined;
    return inc ? { ...svc, status: 'degraded' as const, message: inc.title } : svc;
  });
}

/** The app's benefit statements for the AST team. */
export function appBenefits(app: Pick<Application, 'benefits'>): string[] {
  return parseList(app.benefits);
}

/** The app's screenshot URLs; the first one is its card image. */
export function appScreenshots(app: Pick<Application, 'screenshots'>): string[] {
  return parseList(app.screenshots);
}

export type AppCategory = 'netsuite' | 'custom';

export const APP_CATEGORIES: Array<{ value: AppCategory; label: string }> = [
  { value: 'netsuite', label: 'NetSuite custom app' },
  { value: 'custom', label: 'Custom app' },
];

export function categoryLabel(c: string | null | undefined): string {
  return APP_CATEGORIES.find((x) => x.value === c)?.label ?? 'Custom app';
}

export type Feature = {
  id: string;
  application_id: string;
  title: string;
  detail: string;
  sort_order: number;
};

export type AppRequest = {
  id: string;
  requester_id: string;
  title: string;
  department: string;
  description: string;
  preferred_date: string | null;
  status: string;
  admin_notes: string;
  appointment_at: string | null;
  created_at: string;
  updated_at: string;
  requester_name?: string;
  requester_email?: string;
};

export type ServiceStatus = {
  id: string;
  application_id: string | null;
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'maintenance';
  message: string;
  updated_at: string;
  updated_by: string | null;
};

export type Incident = {
  id: string;
  service_id: string;
  title: string;
  body: string;
  severity: string;
  started_at: string;
  resolved_at: string | null;
};

export type AccessEntry = {
  id: string;
  cidr: string;
  label: string;
  created_at: string;
  created_by: string | null;
};

export type AccessSnapshot = {
  enforce: boolean;
  ip: string;
  allowed: boolean;
  entries: AccessEntry[];
  config: string[];
};

export type AppInput = {
  name: string;
  slug?: string;
  department_id: string;
  tagline: string;
  description: string;
  audience: string;
  status: Application['status'];
  url: string | null;
  stack: string;
  version: string;
  sort_order: number;
  logo_url: string | null;
  category: AppCategory;
  screenshots: string[];
  developer: string;
  time_saved: string;
  benefits: string[];
  show_on_status: boolean;
  cost_saved: string;
  impact_note: string;
  features: Array<{ title: string; detail: string }>;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public data?: Record<string, unknown>,
  ) {
    super(message);
  }
}

/** Fired on window when the API says this network is not allowed. */
export const IP_DENIED_EVENT = 'ast:ip-denied';

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    credentials: 'same-origin',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data as { error?: string; code?: string };
    if (d.code === 'ip_denied') {
      window.dispatchEvent(new CustomEvent(IP_DENIED_EVENT, { detail: data }));
    }
    throw new ApiError(d.error || `Request failed (${res.status})`, res.status, d.code, data as Record<string, unknown>);
  }
  return data as T;
}

export const client = {
  access: () => api<{ ip: string; allowed: boolean; enforce: boolean }>('/access'),
  me: () => api<{ user: SessionUser | null; guest?: boolean }>('/auth/me'),
  login: (email: string, password: string) =>
    api<{ user: SessionUser }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => api<{ ok: boolean }>('/auth/logout', { method: 'POST', body: '{}' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ ok: boolean }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  catalog: () =>
    api<{ departments: Department[]; applications: Application[]; features: Feature[] }>('/catalog'),
  appDetail: (slug: string) =>
    api<{ application: Application; department: Department; features: Feature[] }>(`/catalog/apps/${slug}`),
  requests: () => api<{ requests: AppRequest[] }>('/requests'),
  createRequest: (body: {
    title: string;
    department?: string;
    description: string;
    preferredDate?: string | null;
  }) => api<{ request: AppRequest }>('/requests', { method: 'POST', body: JSON.stringify(body) }),
  updateRequest: (
    id: string,
    body: { status?: string; adminNotes?: string; appointmentAt?: string | null },
  ) => api<{ request: AppRequest }>(`/requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  status: () => api<{ services: ServiceStatus[]; incidents: Incident[] }>('/status'),
  updateService: (id: string, body: { status: string; message?: string }) =>
    api<{ service: ServiceStatus }>(`/status/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  addIncident: (serviceId: string, body: { title: string; body?: string; severity?: string }) =>
    api<{ incident: Incident }>(`/status/${serviceId}/incidents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resolveIncident: (id: string) =>
    api<{ ok: boolean }>(`/status/incidents/${id}/resolve`, { method: 'POST', body: '{}' }),
  users: () =>
    api<{
      users: Array<{
        id: string;
        email: string;
        name: string;
        role: Role;
        department: string;
        active: boolean;
        mustChangePassword: boolean;
        createdAt: string;
        lastLoginAt: string | null;
      }>;
    }>('/admin/users'),
  createUser: (body: { email: string; name: string; role?: Role; department?: string }) =>
    api<{ user: { id: string; email: string; name: string; role: Role }; temporaryPassword: string }>(
      '/admin/users',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateUser: (id: string, body: { name?: string; role?: Role; active?: boolean; department?: string }) =>
    api<{ user: unknown }>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createDepartment: (name: string, summary = '') =>
    api<{ department: Department }>('/admin/departments', { method: 'POST', body: JSON.stringify({ name, summary }) }),
  createApp: (body: AppInput) =>
    api<{ application: Application }>('/admin/apps', { method: 'POST', body: JSON.stringify(body) }),
  updateApp: (id: string, body: AppInput) =>
    api<{ application: Application }>(`/admin/apps/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteApp: (id: string) => api<{ ok: boolean }>(`/admin/apps/${id}`, { method: 'DELETE' }),
  adminAccess: () => api<AccessSnapshot>('/admin/access'),
  setAccessEnforce: (enforce: boolean) =>
    api<AccessSnapshot>('/admin/access', { method: 'PUT', body: JSON.stringify({ enforce }) }),
  addAccessEntry: (cidr: string, label: string) =>
    api<AccessSnapshot>('/admin/access/entries', { method: 'POST', body: JSON.stringify({ cidr, label }) }),
  deleteAccessEntry: (id: string) => api<AccessSnapshot>(`/admin/access/entries/${id}`, { method: 'DELETE' }),
  resetPassword: (id: string) =>
    api<{ temporaryPassword: string }>(`/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: '{}',
    }),
};
